import { Link, Navigate, useParams } from 'react-router-dom'
import { Check, Minus, X } from 'lucide-react'
import Seo from '@/components/Seo'
import SiteLayout from '@/components/SiteLayout'
import PageHero from '@/components/PageHero'
import FeatureSection from '@/components/FeatureSection'
import CtaBand from '@/components/CtaBand'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { getRelatedFeatures } from '@/content/features'
import { getComparePage } from '@/content/compare'
import { cardImageOf, heroImageOf, sectionsWithImages } from '@/content/applyImages'
import { SITE_URL } from '@/components/Seo'

function CellValue({ value }) {
  if (value === true) {
    return <Check className="h-5 w-5 text-primary mx-auto" aria-label="Yes" />
  }
  if (value === false) {
    return <X className="h-5 w-5 text-muted-foreground mx-auto" aria-label="No" />
  }
  if (value == null) {
    return <Minus className="h-5 w-5 text-muted-foreground mx-auto" aria-label="N/A" />
  }
  return <span className="text-sm text-muted-foreground">{value}</span>
}

export default function ComparePage() {
  const { slug } = useParams()
  const page = getComparePage(slug)

  if (!page) {
    return <Navigate to="/" replace />
  }

  const related = getRelatedFeatures(page.relatedFeatureSlugs)
  const heroSrc = heroImageOf(page)
  const resolvedSections = sectionsWithImages(page)

  return (
    <SiteLayout>
      <Seo
        title={page.seoTitle}
        description={page.seoDescription}
        path={page.path}
        image={heroSrc}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: page.seoTitle,
          description: page.seoDescription,
          url: `${SITE_URL}${page.path}`,
        }}
      />
      <PageHero
        title={page.title}
        description={page.intro}
        image={heroSrc}
        eyebrow={page.eyebrow}
      />

      <section className="py-16 bg-background">
        <div className="container mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
            <Card>
              <CardHeader>
                <p className="text-sm font-semibold tracking-widest text-muted-foreground uppercase mb-2">
                  {page.leftTag}
                </p>
                <CardTitle>{page.leftLabel}</CardTitle>
                <CardDescription>
                  <ul className="mt-4 space-y-2 text-left">
                    {page.leftPoints.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Minus className="h-4 w-4 shrink-0 mt-1 text-muted-foreground" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-primary/30 bg-slate-50">
              <CardHeader>
                <p className="text-sm font-semibold tracking-widest text-primary uppercase mb-2">
                  {page.rightTag}
                </p>
                <CardTitle>{page.rightLabel}</CardTitle>
                <CardDescription>
                  <ul className="mt-4 space-y-2 text-left">
                    {page.rightPoints.map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <Check className="h-4 w-4 shrink-0 mt-1 text-primary" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardDescription>
              </CardHeader>
            </Card>
          </div>

          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 font-semibold">Capability</th>
                  <th className="px-4 py-3 font-semibold text-center">{page.leftLabel}</th>
                  <th className="px-4 py-3 font-semibold text-center">{page.rightLabel}</th>
                </tr>
              </thead>
              <tbody>
                {page.rows.map((row) => (
                  <tr key={row.feature} className="border-b last:border-0">
                    <td className="px-4 py-3 font-medium">{row.feature}</td>
                    <td className="px-4 py-3 text-center">
                      <CellValue value={row.stripe} />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <CellValue value={row.tillio} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {resolvedSections.map((section, idx) => (
        <FeatureSection
          key={section.title}
          title={section.title}
          description={section.description}
          image={section.image}
          bullets={section.bullets}
          reverse={idx % 2 === 1}
          className={idx % 2 === 0 ? 'bg-slate-50' : 'bg-background'}
        />
      ))}

      {related.length > 0 && (
        <section className="py-16 bg-background">
          <div className="container mx-auto max-w-7xl px-6">
            <h2 className="text-3xl font-bold text-center mb-10">Related features</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((feature) => (
                <Link key={feature.slug} to={feature.path} className="block group">
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="h-40 overflow-hidden">
                      <img
                        src={cardImageOf(feature)}
                        alt={feature.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    <CardHeader>
                      <CardTitle>{feature.title}</CardTitle>
                      <CardDescription>{feature.hubDescription}</CardDescription>
                    </CardHeader>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CtaBand
        title="Keep Stripe. Add restaurant POS."
        description="Connect your existing Stripe account and run orders, tables, and Terminal checkout with Tillio."
      />
    </SiteLayout>
  )
}
